/**
 * Profile photos — the one vocabulary shared by every subject that can have one.
 *
 * Before this existed, each subject type carried its own upload endpoint, its own multipart
 * field name (`files[]` / `files[profilePicture]` / `files[brandLogo]`) and its own client-side
 * copy of the same upload handler, and only users could actually remove a photo. The subject
 * type is now the only thing that varies between them: one route, one service, one component,
 * parameterized by these values.
 *
 * The string values are the S3 key segment (`profile-pictures/{type}/{id}`) AND the URL segment
 * of the photo routes, so they are storage keys — renaming one orphans every photo already
 * stored under the old name. Add new subjects; don't rename existing ones.
 */
export enum PhotoSubjectType {
  /** Internal staff and client portal users (`users` table). */
  Users = 'users',
  /** Borrower/prospect clients (`client_info`) — the logo shown on prospect and client pages. */
  Clients = 'clients',
  /** Third-party entities (`entity_info`) — vendors, lenders, law firms. */
  Entities = 'entities',
  /** CRM companies (`company`) — also the market-news brand logo, keyed on company.id. */
  Company = 'company',
  /**
   * CRM organizations (`organization`) — referral sources, law firms, lenders.
   *
   * Distinct from `entities` even though the organization PAGE can be backed by either: an
   * organization-sourced page keys its logo here, an entity-sourced one keys it under `entities`
   * (see IOrganizationDetail.source). Storing both under one type would collide whenever an
   * organization and an entity happen to share an id.
   */
  Organizations = 'organizations',
  /** CRM people (`person`) — external contacts: client staff, referral sources, lender contacts. */
  People = 'people',
}

export const PHOTO_SUBJECT_TYPES: PhotoSubjectType[] = Object.values(PhotoSubjectType)

export const isPhotoSubjectType = (value: unknown): value is PhotoSubjectType =>
  typeof value === 'string' && PHOTO_SUBJECT_TYPES.includes(value as PhotoSubjectType)

/**
 * Whether a subject's photo is a BRAND MARK rather than a face.
 *
 * Drives the rendered shape: faces are circles, brand marks are rounded squares — the convention
 * the 140px client/prospect logo plate already followed. Derived from the subject rather than
 * passed per call site, because it is a property of the subject, and the two ways of saying it
 * had already drifted apart (a company logo was a circle in the CRM and a rounded square on the
 * client header).
 *
 * A rounded square also stops a wide logo being cropped to fit a circle.
 */
export const isBrandMarkSubject = (subjectType: PhotoSubjectType | undefined): boolean =>
  subjectType !== undefined &&
  subjectType !== PhotoSubjectType.Users &&
  subjectType !== PhotoSubjectType.People

/**
 * The single multipart field name every photo upload uses. The old per-subject names are gone —
 * a caller that sends the wrong field silently uploaded nothing before, because each service
 * only looked for its own key.
 */
export const PHOTO_UPLOAD_FIELD = 'files[photo]'

/** Image types accepted on upload. Enforced server-side; mirrored in the dropzone's `accept`. */
export const PHOTO_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const

export const PHOTO_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'] as const

/** Max accepted upload, in bytes (~5 MB). */
export const PHOTO_MAX_FILE_SIZE = 5 * 1024 * 1024

/**
 * Edge length the editor exports at. The crop is a square canvas operation on the client, so
 * exporting at a fixed size gets the stored thumbnail for free and keeps a native image-resize
 * dependency (sharp/jimp) out of the server image.
 */
export const PHOTO_THUMBNAIL_PX = 256

/** JPEG quality used for the cropped export. */
export const PHOTO_EXPORT_QUALITY = 0.9
